import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Anchor,
  ActionIcon,
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Kbd,
  Loader,
  Modal,
  Notification,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
  useComputedColorScheme
} from "@mantine/core";
import { useMobileDetection } from "../utils/deviceUtils";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { fetchAuthSession } from 'aws-amplify/auth';
import { canEditQuestions, isAdmin } from "../utils/authUtils";
import { api } from "../apiClient";
import QuestionComments from "./QuestionComments";
import {
  IconDeviceFloppy,
  IconAdjustments,
  IconChevronLeftPipe,
  IconChevronRightPipe,
  IconCheck,
  IconX,
  IconClipboard,
  IconAlertTriangle,
  IconExclamationMark,
  IconKeyboard,
  IconLayoutGrid,
  IconPlayerSkipForward,
  IconRefresh
} from '@tabler/icons-react';

const SCRAPER_API_URL = process.env.REACT_APP_SCRAPER_API_URL || 'http://localhost:8000';

function QuestionView({
  currentQuestion,
  currentIndex,
  totalQuestions,
  results,
  onSetResult,
  onNext,
  onPrev,
  onImageClick,
  showAnswer,
  toggleShowAnswer,
  onSetCurrentQuestion,
  selectedCertification,
  questionOrder,
  user,
  statusLabelMap,
  gridModalOpen,
  onOpenGrid,
  onNextUnanswered,
}) {
  const isMobile = useMobileDetection();
  const questionId = currentQuestion.id;
  // 非同期で権限を確認するためのステート
  const [isAuthorizedToEdit, setIsAuthorizedToEdit] = useState(false);
  const [questionHtml, setQuestionHtml] = useState(null);
  const [btQuestionHtml, setBtQuestionHtml] = useState('');
  const [answerHtml, setAnswerHtml] = useState('');
  const [btAnswerHtml, setBtAnswerHtml] = useState('');
  const theme = useMantineTheme();
  // Mantine のプライマリカラーをまとめて参照することで、ボタン配色をカラースキームに追従させる
  const primaryButtonColor = theme.primaryColor;

  const [isSaving, setIsSaving] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const copyFeedbackTimeoutRef = useRef(null);

  // ダーティ検知: エディタに読み込んだ元の HTML を保持する
  const questionOriginalRef = useRef(null);
  const answerOriginalRef = useRef(null);
  const descriptionOriginalRef = useRef(null);

  // admin 権限チェック・再処理モーダル・ヘルプモーダル
  const [isAdminUser,        setIsAdminUser]        = useState(false);
  const [rescrapeModalOpen,  setRescrapeModalOpen]  = useState(false);
  const [helpModalOpen,      setHelpModalOpen]      = useState(false);
  const [rescrapeOptions,    setRescrapeOptions]    = useState(['full']); // 選択中のモード（複数可）
  const [rescrapeStep,       setRescrapeStep]       = useState(0);        // 現在のステップ（1始まり）
  const [rescrapeTotalSteps, setRescrapeTotalSteps] = useState(0);        // 総ステップ数
  const [isRescraping,       setIsRescraping]       = useState(false);
  const [rescrapeJobStatus,  setRescrapeJobStatus]  = useState(null); // null | 'running' | 'success' | 'error'
  const [rescrapeError,      setRescrapeError]      = useState(null);
  const [descriptionHtml,    setDescriptionHtml]    = useState(null); // null = prop 優先、string = 再処理後の値
  const rescrapeIntervalRef = useRef(null);

  // ユーザーのロールを取得し、編集権限の有無を更新する
  useEffect(() => {
    const fetchAuthorization = async () => {
      const result = await canEditQuestions(user);
      setIsAuthorizedToEdit(result);
    };
    fetchAuthorization();
  }, [user]);

  // admin 権限チェック
  useEffect(() => {
    const checkAdmin = async () => {
      const result = await isAdmin(user);
      setIsAdminUser(result);
    };
    checkAdmin();
  }, [user]);

  // 問題が切り替わるたびに question_html / answer_html を個別取得する
  useEffect(() => {
    if (!questionId) return;
    setQuestionHtml(null);
    setBtQuestionHtml('');
    setAnswerHtml('');
    setBtAnswerHtml('');
    setDescriptionHtml(null);
    api.get(`/questions/${questionId}/html`)
      .then((data) => {
        setQuestionHtml(data?.question ?? '');
        setBtQuestionHtml(data?.bt_question ?? '');
        setAnswerHtml(data?.answer ?? '');
        setBtAnswerHtml(data?.bt_answer ?? '');
        setDescriptionHtml(data?.description ?? '');
      })
      .catch(() => setQuestionHtml(''));
  }, [questionId]);



  // ステータスコードを表示用ラベルへ変換する
  const resolveStatusLabel = useCallback(
    (status) => {
      if (!status) {
        return "未回答";
      }
      return statusLabelMap?.[status] ?? status;
    },
    [statusLabelMap]
  );

  const extractTextAndImages = useCallback((value) => {
    if (!value) {
      return { text: "", imageUrls: [] };
    }

    const normalizeUrls = (urls) => {
      return Array.from(new Set(urls.filter(Boolean)));
    };

    const collectFromElement = (element) => {
      if (!element) {
        return { text: "", imageUrls: [] };
      }

      const textContent = element.textContent || element.innerText || "";
      const urls = Array.from(element.querySelectorAll("img"))
        .map((img) =>
          img.getAttribute("data-src") ||
          img.getAttribute("data-url") ||
          img.getAttribute("data-image") ||
          img.getAttribute("src") ||
          img.src
        )
        .filter(Boolean);

      return {
        text: textContent.trim(),
        imageUrls: normalizeUrls(urls),
      };
    };

    if (typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      const doc = parser.parseFromString(value, "text/html");
      return collectFromElement(doc.body || doc);
    }

    if (typeof document !== "undefined") {
      const tempElement = document.createElement("div");
      tempElement.innerHTML = value;
      return collectFromElement(tempElement);
    }

    const textOnly = value.replace(/<[^>]*>/g, " ").trim();
    const imageMatches = Array.from(
      value.matchAll(
        /<img[^>]+(?:src|data-src|data-url|data-image)\s*=\s*"([^"]+)"[^>]*>/gi
      )
    ).map((match) => match[1]);

    return { text: textOnly, imageUrls: normalizeUrls(imageMatches) };
  }, []);

  // 問題文のエディタ
  const questionEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      Subscript,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'pasted-image',
        },
      }),
    ],
    content: "",
    editable: isAuthorizedToEdit,
  });

  // 解答文のエディタ
  const answerEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      Subscript,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'pasted-image',
        },
      }),
    ],
    content: "",
    editable: isAuthorizedToEdit,
  });

  // 解説文のエディタ
  const descriptionEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      Subscript,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'pasted-image',
        },
      }),
    ],
    content: currentQuestion.description || "",
    editable: isAuthorizedToEdit,
  });

  const showFeedback = useCallback((feedback) => {
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
    }

    const duration = feedback?.duration ?? 2500;

    setCopyFeedback(feedback);

    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setCopyFeedback(null);
      copyFeedbackTimeoutRef.current = null;
    }, duration);
  }, []);

  const handleNextUnansweredClick = () => {
    const found = onNextUnanswered?.();
    if (!found) {
      showFeedback({
        message: '未回答の問題はありません',
        color: 'blue',
        icon: <IconCheck size={18} />,
      });
    }
  };

  const handleCopyQuestion = useCallback(async () => {
    if (
      typeof navigator === "undefined" ||
      !navigator.clipboard ||
      (typeof navigator.clipboard.writeText !== "function" &&
        typeof navigator.clipboard.write !== "function")
    ) {
      showFeedback({
        message: "クリップボードにコピーできませんでした。ブラウザの設定を確認してください。",
        color: "red",
        icon: <IconX size={18} />,
        duration: 4000,
      });
      return;
    }

    const clipboard = navigator.clipboard;

    const currentQuestionHtml = questionEditor?.getHTML?.() ?? questionHtml ?? btQuestionHtml ?? '';
    const { text: questionText, imageUrls: extractedQuestionImages } =
      extractTextAndImages(currentQuestionHtml);

    const questionImages = Array.from(new Set(extractedQuestionImages));

    const questionSection = [];
    if (questionText) {
      questionSection.push(questionText);
    }
    questionImages.forEach((src, index) => {
      questionSection.push(
        `画像${questionImages.length > 1 ? `${index + 1}` : ""}: ${src}`
      );
    });

    const allImageUrls = Array.from(new Set(questionImages.filter(Boolean)));

    const combinedSections = [];
    if (questionSection.length > 0) {
      combinedSections.push(["# 問題文", ...questionSection].join("\n"));
    }

    const combinedText = combinedSections.join("\n\n");

    if (!combinedText) {
      showFeedback({
        message: "コピーする内容がありません。",
        color: "yellow",
        icon: <IconAlertTriangle size={18} />,
        duration: 3000,
      });
      return;
    }

    const clipboardItemCtor =
      (typeof window !== "undefined" && window.ClipboardItem) ||
      (typeof ClipboardItem !== "undefined" ? ClipboardItem : undefined);
    const supportsRichClipboard =
      !!clipboardItemCtor && typeof clipboard.write === "function";
    const hadImages = allImageUrls.length > 0;

    const escapeHtml = (input) =>
      input
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const textToHtmlParagraph = (value) =>
      `<p>${escapeHtml(value).replace(/\n/g, "<br />")}</p>`;

    const buildHtmlContent = (imageMap) => {
      const htmlSections = [];

      if (questionSection.length > 0) {
        const questionHtmlParts = [];

        if (questionText) {
          questionHtmlParts.push(textToHtmlParagraph(questionText));
        }

        questionImages.forEach((src, index) => {
          const imageData = imageMap.get(src);
          const resolvedSrc = imageData?.dataUrl || src;
          questionHtmlParts.push(
            `<figure><figcaption>画像${
              questionImages.length > 1 ? `${index + 1}` : ""
            }</figcaption><img src="${resolvedSrc}" alt="問題画像${
              index + 1
            }" /></figure>`
          );
        });

        htmlSections.push(
          `<section><h2>問題文</h2>${questionHtmlParts.join("")}</section>`
        );
      }

      if (htmlSections.length === 0) {
        return "";
      }

      return `<article>${htmlSections.join("")}</article>`;
    };

    const guessMimeType = (url) => {
      const cleanUrl = url.split("?")[0] || "";
      const extension = cleanUrl.split(".").pop()?.toLowerCase();
      switch (extension) {
        case "jpg":
        case "jpeg":
          return "image/jpeg";
        case "gif":
          return "image/gif";
        case "webp":
          return "image/webp";
        case "svg":
          return "image/svg+xml";
        case "png":
        default:
          return "image/png";
      }
    };

    const blobToDataUrl = async (blob) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });

    if (supportsRichClipboard) {
      const imageDataMap = new Map();
      let successfulImages = [];

      if (hadImages) {
        const fetchedImages = await Promise.all(
          allImageUrls.map(async (url) => {
            try {
              const response = await fetch(url, {
                mode: "cors",
                credentials: "omit",
              });

              if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status}`);
              }

              const blob = await response.blob();
              const mimeType = blob.type || guessMimeType(url);
              const dataUrl = await blobToDataUrl(blob);

              const imageInfo = { url, blob, mimeType, dataUrl };
              imageDataMap.set(url, imageInfo);
              return imageInfo;
            } catch (imageError) {
              console.warn(
                `画像をクリップボードに追加できませんでした (${url}):`,
                imageError
              );
              return { url, error: imageError };
            }
          })
        );

        successfulImages = fetchedImages.filter((item) => !item.error);
      }

      const htmlContent = buildHtmlContent(imageDataMap);

      const baseClipboardTypes = {
        "text/plain": new Blob([combinedText], { type: "text/plain" }),
      };

      if (htmlContent) {
        baseClipboardTypes["text/html"] = new Blob([htmlContent], {
          type: "text/html",
        });
      }

      if (successfulImages[0]) {
        baseClipboardTypes[successfulImages[0].mimeType] = successfulImages[0].blob;
      }

      const clipboardItems = [new clipboardItemCtor(baseClipboardTypes)];

      if (successfulImages.length > 1) {
        successfulImages.slice(1).forEach((image) => {
          clipboardItems.push(
            new clipboardItemCtor({ [image.mimeType]: image.blob })
          );
        });
      }

      try {
        await clipboard.write(clipboardItems);

        console.log("[QuestionCopy] コピー完了", {
          text: combinedText,
          imageCount: successfulImages.length,
          imageSources: successfulImages.map((image) => ({
            url: image.url,
            mimeType: image.mimeType,
            size: image.blob.size,
          })),
          usedHtml: Boolean(htmlContent),
        });

        if (successfulImages.length === allImageUrls.length) {
          showFeedback({
            message: "問題文・選択肢と画像をコピーしました。",
            color: "green",
            icon: <IconCheck size={18} />,
          });
          return;
        }

        if (successfulImages.length > 0) {
          showFeedback({
            message: "テキストと一部の画像をコピーしました。",
            color: "yellow",
            icon: <IconAlertTriangle size={18} />,
            duration: 4000,
          });
          return;
        }

        if (!hadImages) {
          showFeedback({
            message: "問題文と選択肢をコピーしました。",
            color: "green",
            icon: <IconCheck size={18} />,
          });
          return;
        }

        showFeedback({
          message:
            "テキストはコピーしましたが、画像はブラウザまたはCORS設定により追加できませんでした。",
          color: "yellow",
          icon: <IconAlertTriangle size={18} />,
          duration: 4000,
        });
        return;
      } catch (writeError) {
        console.error("Failed to copy rich clipboard:", writeError);
      }
    }

    try {
      await clipboard.writeText(combinedText);

      console.log("[QuestionCopy] テキストのみコピー", {
        text: combinedText,
        imageUrls: allImageUrls,
      });

      if (hadImages) {
        showFeedback({
          message:
            "テキストのみコピーしました。画像はブラウザの制限により含まれていません。",
          color: "yellow",
          icon: <IconAlertTriangle size={18} />,
          duration: 4000,
        });
        return;
      }

      showFeedback({
        message: "問題文と選択肢をコピーしました。",
        color: "green",
        icon: <IconCheck size={18} />,
      });
    } catch (error) {
      console.error("Failed to copy question:", error);
      showFeedback({
        message: "コピーに失敗しました。ブラウザの設定を確認してください。",
        color: "red",
        icon: <IconX size={18} />,
        duration: 4000,
      });
    }
  }, [
    questionEditor,
    questionHtml,
    btQuestionHtml,
    extractTextAndImages,
    showFeedback,
    selectedCertification,
    questionId,
  ]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (rescrapeIntervalRef.current) {
        clearInterval(rescrapeIntervalRef.current);
      }
    };
  }, []);


  // 権限の変化に応じてリッチテキストエディタの編集可否を同期させる
  useEffect(() => {
    [questionEditor, answerEditor, descriptionEditor].forEach((editorInstance) => {
      if (editorInstance) {
        editorInstance.setEditable(isAuthorizedToEdit);
      }
    });
  }, [isAuthorizedToEdit, questionEditor, answerEditor, descriptionEditor]);

  // 管理者: question (日本語 HTML) をエディタにセット
  useEffect(() => {
    if (questionEditor && isAuthorizedToEdit && questionHtml !== null) {
      questionEditor.commands.setContent(questionHtml || "");
      // TipTap が正規化した HTML をベースラインとして保持（生の API 値と差が出ないよう）
      questionOriginalRef.current = questionEditor.getHTML();
    }
  }, [questionHtml, questionEditor, isAuthorizedToEdit]);

  // 解答: answer / bt_answer が取得できたらエディタにセット
  useEffect(() => {
    if (answerEditor) {
      answerEditor.commands.setContent(answerHtml || btAnswerHtml || "");
      answerOriginalRef.current = answerEditor.getHTML();
    }
  }, [answerHtml, btAnswerHtml, answerEditor]);

  // 解説文のエディタの内容が変更されたときにcurrentQuestionを更新
  useEffect(() => {
    if (descriptionEditor && currentQuestion) {
      descriptionEditor.commands.setContent(currentQuestion.description || "");
      descriptionOriginalRef.current = descriptionEditor.getHTML();
    }
  }, [currentQuestion, descriptionEditor]);

  // 再処理後の descriptionHtml をエディタに反映する
  useEffect(() => {
    if (descriptionEditor && descriptionHtml !== null) {
      descriptionEditor.commands.setContent(descriptionHtml || '');
      descriptionOriginalRef.current = descriptionEditor.getHTML();
    }
  }, [descriptionHtml, descriptionEditor]);

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = (event) => {
      // 入力中はスキップ（TipTap エディタ含む）
      const tag = event.target.tagName;
      const isEditing =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        event.target.contentEditable === 'true';
      if (isEditing) return;

      // モーダル表示中はスキップ
      if (rescrapeModalOpen || gridModalOpen || helpModalOpen) return;

      // Space / Enter: ボタン・リンクにフォーカスがある場合はネイティブ動作を優先
      if (event.key === ' ' || event.key === 'Enter') {
        if (tag === 'BUTTON' || tag === 'A') return;
        event.preventDefault();
        toggleShowAnswer();
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          questionOrder === 'last' ? onNext() : onPrev();
          break;
        case 'ArrowRight':
          questionOrder === 'last' ? onPrev() : onNext();
          break;
        case '1':
          onSetResult(questionId, 'correct');
          break;
        case '2':
          onSetResult(questionId, 'incorrect');
          break;
        case '3':
          onSetResult(questionId, 'excluded');
          break;
        case '?':
          setHelpModalOpen(true);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [questionId, questionOrder, rescrapeModalOpen, gridModalOpen, helpModalOpen, onPrev, onNext, toggleShowAnswer, onSetResult]);

  // 選択肢の排他制御（すべて修正は単一選択のみ）
  const toggleRescrapeOption = (value) => {
    if (value === 'full') {
      setRescrapeOptions(['full']);
    } else {
      setRescrapeOptions((prev) => {
        const without = prev.filter((m) => m !== 'full');
        if (without.includes(value)) {
          return without.filter((m) => m !== value);
        }
        return [...without, value];
      });
    }
  };

  // 選択中のオプションを実行順に並べて返す
  const getOrderedModes = (options) => {
    if (options.includes('full')) return ['full'];
    return ['scrape_translate', 'translate_only', 'description_only'].filter((m) =>
      options.includes(m)
    );
  };

  // 単体問題の再処理（複数モードを順次実行）
  const handleRescrape = async () => {
    const orderedModes = getOrderedModes(rescrapeOptions);
    if (orderedModes.length === 0) return;

    setIsRescraping(true);
    setRescrapeJobStatus('running');
    setRescrapeError(null);
    setRescrapeStep(0);
    setRescrapeTotalSteps(orderedModes.length);

    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.idToken?.toString();
      if (!token) throw new Error('ログインが必要です');

      for (let i = 0; i < orderedModes.length; i++) {
        setRescrapeStep(i + 1);

        const res = await fetch(`${SCRAPER_API_URL}/scraper/questions/rescrape`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body:    JSON.stringify({ question_id: questionId, mode: orderedModes[i] }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || '再処理の起動に失敗しました');
        }
        const { job_id: jobId } = await res.json();

        // ジョブ完了まで待機
        await new Promise((resolve, reject) => {
          rescrapeIntervalRef.current = setInterval(async () => {
            try {
              const jobData = await api.get('/scrape-jobs', { id: jobId });
              if (jobData?.status === 'completed') {
                clearInterval(rescrapeIntervalRef.current);
                rescrapeIntervalRef.current = null;
                resolve();
              } else if (jobData?.status === 'failed') {
                clearInterval(rescrapeIntervalRef.current);
                rescrapeIntervalRef.current = null;
                reject(new Error('再処理が失敗しました。管理画面のジョブ一覧でログを確認してください。'));
              }
            } catch (_) {
              // ポーリングエラーは無視して継続
            }
          }, 3000);
        });
      }

      // 全ステップ完了
      setRescrapeJobStatus('success');
      setIsRescraping(false);
      // 1.5 秒でモーダルを閉じて問題データ（解説含む）を再取得
      setTimeout(() => {
        setRescrapeModalOpen(false);
        setRescrapeJobStatus(null);
        setRescrapeStep(0);
        api.get(`/questions/${questionId}/html`)
          .then((freshData) => {
            setQuestionHtml(freshData?.question ?? '');
            setBtQuestionHtml(freshData?.bt_question ?? '');
            setAnswerHtml(freshData?.answer ?? '');
            setBtAnswerHtml(freshData?.bt_answer ?? '');
            if (freshData?.description !== undefined) {
              setDescriptionHtml(freshData.description ?? '');
            }
          })
          .catch(() => {});
      }, 1500);
    } catch (err) {
      if (rescrapeIntervalRef.current) {
        clearInterval(rescrapeIntervalRef.current);
        rescrapeIntervalRef.current = null;
      }
      setRescrapeJobStatus('error');
      setRescrapeError(err.message);
      setIsRescraping(false);
    }
  };

  // 保存ボタンがクリックされたときの処理
  const handleSave = async () => {
    // 編集権限がない場合は保存を拒否
    if (!isAuthorizedToEdit) {
      alert('問題の編集権限がありません。');
      return;
    }

    setIsSaving(true);

    try {
      const currentQuestion_ = questionEditor?.getHTML() ?? '';
      const currentAnswer = answerEditor?.getHTML() ?? '';
      const currentDescription = descriptionEditor?.getHTML() ?? '';

      // ダーティ検知: 元の HTML と比較して変更のあったフィールドのみ送信
      const payload = {};
      if (currentQuestion_ !== questionOriginalRef.current) payload.question = currentQuestion_;
      if (currentAnswer !== answerOriginalRef.current) payload.answer = currentAnswer;
      if (currentDescription !== descriptionOriginalRef.current) payload.description = currentDescription;

      if (Object.keys(payload).length === 0) {
        showFeedback({
          message: '変更がありません。',
          color: 'gray',
          icon: <IconCheck size={18} />,
          duration: 2000,
        });
        return;
      }

      await api.put(`/questions/${questionId}/html`, payload);

      // 保存後に state と original ref を更新
      if (payload.question !== undefined) {
        setQuestionHtml(payload.question);
        questionOriginalRef.current = payload.question;
      }
      if (payload.answer !== undefined) {
        setAnswerHtml(payload.answer);
        answerOriginalRef.current = payload.answer;
      }
      if (payload.description !== undefined) {
        setDescriptionHtml(payload.description);
        descriptionOriginalRef.current = payload.description;
      }

      showFeedback({
        message: '保存しました。',
        color: 'teal',
        icon: <IconCheck size={18} />,
        duration: 2500,
      });
    } catch (err) {
      console.error("予期しないエラーが発生しました:", err);
      showFeedback({
        message: '保存に失敗しました。',
        color: 'red',
        icon: <IconX size={18} />,
        duration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const colorScheme = useComputedColorScheme();

  const richTextBorderColor = theme.colors.gray[colorScheme === "dark" ? 8 : 3];

  return (
    <>
    <Modal
      opened={rescrapeModalOpen}
      onClose={() => {
        if (!isRescraping) {
          setRescrapeModalOpen(false);
          setRescrapeJobStatus(null);
          setRescrapeError(null);
        }
      }}
      title="単体問題の再処理"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">問題 ID: {questionId}</Text>
        <Stack gap="xs">
          <Text size="sm" fw={500}>処理モード</Text>
          <Checkbox
            label="すべて修正"
            description="再スクレイピング + 翻訳 + 解説生成"
            checked={rescrapeOptions.includes('full')}
            onChange={() => toggleRescrapeOption('full')}
            disabled={isRescraping}
          />
          <Text size="xs" c="dimmed" mt={2}>以下は複数選択可</Text>
          <Checkbox
            label="問題/解答のスクレイピング/翻訳"
            description="問題・解答を再取得して翻訳します"
            checked={rescrapeOptions.includes('scrape_translate')}
            onChange={() => toggleRescrapeOption('scrape_translate')}
            disabled={isRescraping}
          />
          <Checkbox
            label="翻訳のみ"
            description="問題文を翻訳します"
            checked={rescrapeOptions.includes('translate_only')}
            onChange={() => toggleRescrapeOption('translate_only')}
            disabled={isRescraping}
          />
          <Checkbox
            label="解説生成のみ"
            description="解説を再生成します"
            checked={rescrapeOptions.includes('description_only')}
            onChange={() => toggleRescrapeOption('description_only')}
            disabled={isRescraping}
          />
        </Stack>

        {rescrapeJobStatus === 'running' && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm">
              {rescrapeTotalSteps > 1
                ? `ステップ ${rescrapeStep}/${rescrapeTotalSteps} 処理中...`
                : '処理中です。しばらくお待ちください...'}
            </Text>
          </Group>
        )}
        {rescrapeJobStatus === 'success' && (
          <Alert color="green" variant="light">
            再処理が完了しました。問題データを更新します...
          </Alert>
        )}
        {rescrapeJobStatus === 'error' && rescrapeError && (
          <Alert color="red" variant="light">{rescrapeError}</Alert>
        )}

        <Group justify="flex-end">
          <Button
            variant="subtle"
            onClick={() => {
              if (!isRescraping) {
                setRescrapeModalOpen(false);
                setRescrapeJobStatus(null);
                setRescrapeError(null);
              }
            }}
            disabled={isRescraping}
          >
            キャンセル
          </Button>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={handleRescrape}
            loading={isRescraping}
            disabled={isRescraping || rescrapeJobStatus === 'success' || rescrapeOptions.length === 0}
          >
            実行
          </Button>
        </Group>
      </Stack>
    </Modal>

    <Modal
      opened={helpModalOpen}
      onClose={() => setHelpModalOpen(false)}
      title="キーボードショートカット"
      size="sm"
      centered
    >
      <Stack gap="xs">
        {[
          { keys: ['←'],           desc: '前の問題' },
          { keys: ['→'],           desc: '次の問題' },
          { keys: ['Space', 'Enter'], desc: '解答・解説の表示/非表示' },
          { keys: ['1'],           desc: '正解' },
          { keys: ['2'],           desc: '不正解' },
          { keys: ['3'],           desc: '除外' },
          { keys: ['?'],           desc: 'このヘルプを表示' },
        ].map(({ keys, desc }) => (
          <Group key={desc} justify="space-between">
            <Text size="sm">{desc}</Text>
            <Group gap={4}>
              {keys.map((k) => <Kbd key={k}>{k}</Kbd>)}
            </Group>
          </Group>
        ))}
      </Stack>
    </Modal>

    <Box
      p="xl"
      // Mantine のボディカラーを直接参照することで、ダーク/ライト切替時に確実に背景色が追従する
      bg="var(--mantine-color-body)"
      // テキストもボディ色にマッチするようテーマの CSS 変数を利用
      sx={{
        color: "var(--mantine-color-text)",
      }}
    >
      {copyFeedback && (
        <Box
          sx={{
            position: "fixed",
            top: 24,
            right: 24,
            zIndex: 2000,
            maxWidth: 360,
          }}
        >
          <Notification color={copyFeedback.color} icon={copyFeedback.icon} withCloseButton={false}>
            {copyFeedback.message}
          </Notification>
        </Box>
      )}
      <Group position="right">
        <Text size="sm" color="dimmed">
          {`${questionOrder === 'last' ? totalQuestions - currentIndex : currentIndex + 1} / ${totalQuestions}`}
        </Text>
      </Group>
      <Box mb="md">
        <Title order={4}>ID: {questionId}　　Topic: {currentQuestion.topic}　　Question: {currentQuestion.number}　　ステータス: {resolveStatusLabel(results[questionId])}</Title>
      </Box>

      <Box mb="md">
        <Title order={4}>
          <Anchor 
            href={currentQuestion.url}
            target="_blank"
            rel="noopener noreferrer"
          >
              ExamTopicsのリンク
          </Anchor>
        </Title>
      </Box>

      <Box mb="md">
        {isAuthorizedToEdit && !isMobile ? (
          <RichTextEditor
            editor={questionEditor}
            styles={{
              root: {
                label: "RichTextEditor",
                border: `1px solid ${richTextBorderColor}`,
                borderRadius: "4px",
                padding: "8px",
                margin: "8px 0",
              },
            }}
          >
            <RichTextEditor.Content />
          </RichTextEditor>
        ) : (
          <div
            className="question-html-body"
            dangerouslySetInnerHTML={{ __html: questionHtml || btQuestionHtml || '' }}
            style={{ lineHeight: 1.7 }}
          />
        )}
      </Box>


      <Box mb="md">
        <Button
          variant="filled"
          color={primaryButtonColor}
          onClick={toggleShowAnswer}
        >
          {showAnswer ? "解答・解説を隠す" : "解答・解説を見る"}
        </Button>
      </Box>

      {showAnswer && (
        <>
          <Box mb="md">
            <Title order={5}>解答:</Title>
            {isAuthorizedToEdit && !isMobile ? (
              <RichTextEditor
                editor={answerEditor}
                styles={{
                  root: {
                    label: "RichTextEditor",
                    border: `1px solid ${richTextBorderColor}`,
                    borderRadius: "4px",
                    padding: "8px",
                    margin: "16px",
                  },
                }}
              >
                <RichTextEditor.Content />
              </RichTextEditor>
            ) : (
              <div
                className="question-html-body"
                dangerouslySetInnerHTML={{ __html: answerHtml || btAnswerHtml || '' }}
                style={{ lineHeight: 1.7 }}
              />
            )}
          </Box>
          <Box mb="md">
            <Title order={5}>解説:</Title>
            {isAuthorizedToEdit && !isMobile ? (
              <RichTextEditor
                editor={descriptionEditor}
                styles={{
                  root: {
                    label: "RichTextEditor",
                    border: `1px solid ${richTextBorderColor}`,
                    borderRadius: "4px",
                    padding: "8px",
                    margin: "16px",
                  },
                }}
              >
                <RichTextEditor.Content />
              </RichTextEditor>
            ) : (
              <div
                className="question-html-body"
                dangerouslySetInnerHTML={{ __html: descriptionHtml !== null ? descriptionHtml : (currentQuestion.description || '') }}
                style={{ lineHeight: 1.7 }}
              />
            )}
          </Box>
        </>
      )}

      <QuestionComments questionId={questionId} user={user} />

      {/* スティッキーバー用スペーサー */}
      <Box style={{ height: 72 }} />

      {/* スティッキー操作バー */}
      <Box
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: 'var(--mantine-color-body)',
          borderTop: '1px solid var(--app-color-border, #f0eee6)',
          padding: '10px 16px',
        }}
      >
        <Group justify="space-between" wrap="nowrap" gap="xs">
          {/* 左: 条件変更（フィルター画面へ戻る）・コピー・保存・再処理 */}
          <Group gap="xs" wrap="nowrap">
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconAdjustments size={15} />}
              onClick={() => onPrev(false)}
            >
              条件変更
            </Button>
            <Tooltip label="問題文をコピー" withArrow>
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={handleCopyQuestion}
              >
                <IconClipboard size={17} />
              </ActionIcon>
            </Tooltip>
            {isAuthorizedToEdit && (
              <Tooltip label="保存" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <IconDeviceFloppy size={17} />
                </ActionIcon>
              </Tooltip>
            )}
            {isAdminUser && (
              <Tooltip label="単体問題の再処理" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => {
                    setRescrapeJobStatus(null);
                    setRescrapeError(null);
                    setRescrapeModalOpen(true);
                  }}
                >
                  <IconRefresh size={17} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="キーボードショートカット" withArrow>
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={() => setHelpModalOpen(true)}
              >
                <IconKeyboard size={17} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* 中央: 前の問題・問題一覧・次の未回答・次の問題（移動系を集約） */}
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="前の問題" withArrow>
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={questionOrder === 'last' ? onNext : onPrev}
              >
                <IconChevronLeftPipe size={17} />
              </ActionIcon>
            </Tooltip>
            {!isMobile && (
              <Tooltip label="問題一覧" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={onOpenGrid}
                >
                  <IconLayoutGrid size={17} />
                </ActionIcon>
              </Tooltip>
            )}
            {!isMobile && (
              <Tooltip label="次の未回答へ" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={handleNextUnansweredClick}
                >
                  <IconPlayerSkipForward size={17} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="次の問題" withArrow>
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={questionOrder === 'last' ? onPrev : onNext}
              >
                <IconChevronRightPipe size={17} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* 右: 正解・不正解・除外 */}
          <Group gap="xs" wrap="nowrap">
            <Button
              color="teal"
              variant={results[questionId] === 'correct' ? 'filled' : 'light'}
              size="sm"
              leftSection={<IconCheck size={15} />}
              onClick={() => onSetResult(questionId, 'correct')}
            >
              <Text visibleFrom="sm" span>正解</Text>
            </Button>
            <Button
              color="red"
              variant={results[questionId] === 'incorrect' ? 'filled' : 'light'}
              size="sm"
              leftSection={<IconX size={15} />}
              onClick={() => onSetResult(questionId, 'incorrect')}
            >
              <Text visibleFrom="sm" span>不正解</Text>
            </Button>
            <Button
              color="yellow"
              variant={results[questionId] === 'excluded' ? 'filled' : 'light'}
              size="sm"
              leftSection={<IconExclamationMark size={15} />}
              onClick={() => onSetResult(questionId, 'excluded')}
            >
              <Text visibleFrom="sm" span>除外</Text>
            </Button>
          </Group>
        </Group>
      </Box>

    </Box>
    </>
  );
}

export default QuestionView;
